import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgwNTY3MTk5fDA&ixlib=rb-4.1.0&q=85";

const SSO_ERRORS = {
  nouser: "This Microsoft account is not provisioned in the app. Ask an admin to add or sync your user first.",
  inactive: "Your license is inactive. Contact your administrator.",
  token: "Microsoft sign-in could not be completed. Please try again.",
  state: "Your sign-in session expired. Please try again.",
};

export default function Login() {
  const { user, login, loginWithToken } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ssoAvailable, setSsoAvailable] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  // SSO availability
  useEffect(() => {
    api.get("/auth/m365/status").then((r) => setSsoAvailable(!!r.data.available)).catch(() => {});
  }, []);

  // Handle the Microsoft callback: token in the URL hash, errors in the query string.
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = hash.get("sso_token");
    if (token) {
      window.history.replaceState(null, "", window.location.pathname);
      loginWithToken(token).then(() => navigate("/")).catch(() => setError(SSO_ERRORS.token));
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const ssoErr = params.get("sso_error");
    if (ssoErr) {
      setError(SSO_ERRORS[ssoErr] || "Microsoft sign-in failed. Please try again.");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [loginWithToken, navigate]);

  const startSso = async () => {
    setSsoBusy(true);
    setError("");
    try {
      const { data } = await api.get("/auth/m365/login", { params: { origin: window.location.origin } });
      window.location.href = data.authorize_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSsoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand / hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 bg-white/10 backdrop-blur flex items-center justify-center font-heading font-bold text-xl border border-white/20">
            {(branding?.app_name || "M")[0]}
          </div>
          <span className="font-heading font-semibold text-lg tracking-tight">{branding?.app_name || "Meridian"}</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tighter leading-[1.05]">
            Commission & banking, measured to the rand.
          </h1>
          <p className="mt-5 text-white/70 text-sm leading-relaxed">
            Capture daily banking, run the tiered commission engine, and give every fee earner a clear line of sight — all governed by role.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.15em]">
          <ShieldCheck className="h-4 w-4" /> Microsoft 365 SSO ready
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="h-10 w-10 bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-xl">
              {(branding?.app_name || "M")[0]}
            </div>
            <span className="font-heading font-semibold text-lg">{branding?.app_name || "Meridian"}</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Use your work credentials to continue.</p>

          <button
            type="button"
            onClick={startSso}
            disabled={!ssoAvailable || ssoBusy}
            data-testid="sso-btn"
            className={`mt-6 w-full flex items-center justify-center gap-2 border border-border py-2.5 text-sm font-medium transition-colors ${ssoAvailable && !ssoBusy ? "text-foreground hover:bg-secondary cursor-pointer" : "text-muted-foreground cursor-not-allowed"}`}
            title={ssoAvailable ? "Sign in with your Microsoft 365 work account" : "Configure Azure AD in Settings → Integrations to enable"}
          >
            <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="" className="h-4 w-4" />
            {ssoBusy ? "Redirecting to Microsoft…" : "Continue with Microsoft 365"}
          </button>
          <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-muted-foreground">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 rounded-none" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] text-muted-foreground">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-none" required />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2">{error}</div>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-none gap-2">
              {loading ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
